/**
 * USER DOCS: changes to this module should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/index.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting)
 */

// Shared loader for voting session detail inside WorkItemDetailModal tabs —
// the voting session IS the work item (shared UUID), so the work item id is
// the session id. Polls while the session is live (tallies/check-ins move).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSessionDetail,
  type VotingSessionDetail,
} from '@/app/client-zone/modules/total-operations/components/voting/votingApi';

export interface VotingSessionDetailState {
  detail: VotingSessionDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useVotingSessionDetail(sessionId: string | null | undefined): VotingSessionDetailState {
  const [detail, setDetail] = useState<VotingSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const reload = useCallback(async () => {
    const id = sessionIdRef.current;
    if (!id) return;
    try {
      const next = await getSessionDetail(id);
      setDetail(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload, sessionId]);

  // Keep tallies/check-ins fresh while the session is live.
  useEffect(() => {
    if (detail?.session.status !== 'live') return;
    const t = setInterval(() => void reload(), 5000);
    return () => clearInterval(t);
  }, [detail?.session.status, reload]);

  return { detail, loading, error, reload };
}
